import { BoxBase, StackBase } from '@fmc-digital/ui';
import { ImpactedForm } from '@fmc-digital/servicing';
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { withHeaderFooter } from 'components_v2/HOCs/withHeaderFooter/withHeaderFooter';
import { Identifier } from '@types';
import { useSelector } from 'react-redux';
import { selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { auth, config } from '../../../config';
import { EventType, NaturalDisasterConstants } from 'shared-ui-components';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { useQueryClient } from '@tanstack/react-query';
import { ImpactedPropNavigationProps } from '@components_v2/data';
import { useSnackbar } from 'notistack';
import { useRequestForbearanceMutation } from '@hooks/useRequestForbearanceMutation';
import { dispatch } from '@store';
import { clearLoanNDStatus } from '@store/actions/widgets.actions';
import { useSnackBarProps } from '@hooks/useSnackbarProps';

const ImpactedProperty = (): JSX.Element => {
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const theme = useTheme();
    const currentLoan: Identifier = useSelector(selectSelectedLoanNumber);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const successSnackBar = useSnackBarProps({ severity: 'success' });
    const errorSnackBar = useSnackBarProps({ severity: 'error' });

    const apiEvents = useCallback((event: EventType) => {
        logApiEvent({
            apiName: event.apiName,
            action: event.action,
            correlationId: event.correlationId,
            timestamp: event.timestamp,
            loanNumber: event.loanNumber,
            description: event.description,
            type: 'Natural Disaster ACP widget'
        });
    }, []);
    const userEvents = useCallback((event: EventType) => {
        logUserEvent({
            type: 'Natural Disaster ACP widget',
            action: event.action,
            loanNumber: currentLoan.identifier,
            description: event.description
        });
    }, []);

    useEffect(() => {
        logUserEvent({
            type: 'Natural Disaster',
            action: 'HomeAffectedAndWorkAreaAffected',
            loanNumber: currentLoan.identifier,
            description: 'HomeAffected and WorkAreaAffected alert displayed'
        });
    }, [currentLoan.identifier]);

    const requestForbearance = useRequestForbearanceMutation({
        forbearanceApiParams: {
            baseAWSUrl: config.env('natDisasterAWSUrl') + `/${currentLoan.identifier}/natural-disaster/`,
            xApiKey: config.env('xApiKey')
        },
        onComplete: async () => {
            await queryClient.invalidateQueries(['widgets']);
            dispatch(clearLoanNDStatus(currentLoan.identifier));
            navigate('../disaster-assistance', { replace: true });
        },
        logApiEvent: apiEvents,
        logUserEvent: userEvents,
        type: 'Natural Disaster',
        accessToken: auth.getOkta().getAccessToken(),
        currentLoan: currentLoan
    });

    const handleSubmitNDForm = (values: { home: boolean; business: boolean }) => {
        setIsSaving(true);
        const request = {
            borrowerImpacted: 'Y',
            determinationMethod: 'BORROWER NOTIFICATION',
            label: 'No',
            propertyImpactDetermination: values.home.toString() === 'true' ? 'Y' : 'N',
            workplaceImpactDetermination: values.business.toString() === 'true' ? 'Y' : 'N'
        };
        const propertyImpact = values.home.toString() === 'true' ? 'HomeAffectedYesClicked' : 'HomeAffectedNoClicked';
        const workPlaceImpact = values.business.toString() === 'true' ? 'WorkplaceAffectedYesClicked' : 'WorkplaceAffectedNoClicked';
        logUserEvent({
            type: 'Natural Disaster',
            action: `${propertyImpact}and${workPlaceImpact} `,
            loanNumber: currentLoan.identifier,
            description: `${propertyImpact} and ${workPlaceImpact} selected from Natural Disaster alert `
        });
        requestForbearance.mutate(request, {
            onSuccess: () => {
                enqueueSnackbar(NaturalDisasterConstants.SUCCESS_FORBEARANCE_MESSAGE, successSnackBar);
            },
            onError: () => {
                enqueueSnackbar(NaturalDisasterConstants.FAILURE_FORBEARANCE_MESSAGE, errorSnackBar);
            }
        });
    };

    const backToHome = () => {
        navigate('../');
    };

    if (isSaving) return <BoxBase>Loading</BoxBase>;

    return (
        <StackBase
            maxWidth={theme.spacing(107.25)}
            justifySelf="center"
            direction="column"
            margin="0 auto"
            flexGrow={1}
            rowGap={{ xs: theme.spacing(2), md: theme.spacing(4) }}
            padding={{ xs: theme.spacing(2), md: theme.spacing(4) }}
        >
            <ImpactedForm handleSubmitNDForm={handleSubmitNDForm} backToHome={backToHome} />
        </StackBase>
    );
};
export default withHeaderFooter(ImpactedProperty, { ...ImpactedPropNavigationProps, showFooterAds: true });
